import { EmbedVideo } from 'components/step-multimedia/EmbedVideo';
import { ImagesUpload } from 'components/step-multimedia/ImagesUpload';
import { MultimediaSchema } from 'components/step-multimedia/multimedia.schema';
import { ProjectWizardContext } from 'context/Context';
import { Field, Form as FormFormik, Formik } from 'formik';
import lodash from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { uploadImageProject } from 'redux/projectsSlice';
import messageHandler from 'utils/messageHandler';

const Multimedia = () => {
  const { data, setData, handleSubmit, setStepStatus, yupSubmitRef } =
    useContext(ProjectWizardContext);

  const dispatch = useDispatch();

  const [initialValues, setInitialValues] = useState({
    video: data.video,
    video360: data.video360
  });

  const [showVideos, setShowVideos] = useState({
    video: false,
    video360: false
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingHouseMap, setUploadingHouseMap] = useState(false);

  useEffect(() => {
    setInitialValues({
      video: data.video,
      video360: data.video360
    });
  }, [data]);

  const handleImages = async (files, type) => {
    if (type === 'image') {
      handleUploadImage(files);
    } else if (type === 'houseMap') {
      handleUploadHouseMap(files);
    }
  };

  const setNewImagesInData = (newImages, accessor) => {
    setData(prev => ({
      ...prev,
      [accessor]: [...prev[accessor], ...newImages]
    }));

    setStepStatus(prev => ({
      ...prev,
      [accessor]: [...prev[accessor], ...newImages]
    }));
  };

  const replaceImagesInData = (newImages, accessor) => {
    setData(prev => ({
      ...prev,
      [accessor]: newImages
    }));

    setStepStatus(prev => ({
      ...prev,
      [accessor]: newImages
    }));
  };

  const handleUploadImage = async files => {
    setUploadingImage(true);

    let uploadSuccessfully = true;
    let newImages = [];

    for (let img of files) {
      let formData = new FormData();
      formData.append('image', img);

      try {
        const response = await dispatch(uploadImageProject(data._id, formData));

        if (response.status > 299) {
          uploadSuccessfully = false;
        }
        if (response.status === 200 && response.data) {
          newImages.push(response.data[0]);
        }
      } catch (e) {
        console.log('Error: ', e);
      }
    }

    setNewImagesInData(newImages, 'images');

    if (uploadSuccessfully) {
      messageHandler('success', 'Imágenes cargadas correctamente.');
    } else if (!uploadSuccessfully) {
      messageHandler(
        'error',
        'Error al cargar las imágenes. Por favor, inténtelo nuevamente.'
      );
    }

    setUploadingImage(false);
  };

  const handleUploadHouseMap = async files => {
    setUploadingHouseMap(true);

    let uploadSuccessfully = true;
    let newImages = [];
    for (let img of files) {
      let formData = new FormData();
      formData.append('houseMap', img);

      try {
        const response = await dispatch(uploadImageProject(data._id, formData));
        if (response.status > 299) {
          uploadSuccessfully = false;
        }
        if (response.status === 200 && response.data) {
          newImages.push(response.data[0]);
        }
      } catch (e) {
        console.log('Error: ', e);
      }
    }

    setNewImagesInData(newImages, 'houseMaps');

    if (uploadSuccessfully) {
      messageHandler('success', 'Planos cargados correctamente.');
    } else if (!uploadSuccessfully) {
      messageHandler(
        'error',
        'Error al cargar las imágenes. Por favor, inténtelo nuevamente.'
      );
    }

    setUploadingHouseMap(false);
  };

  const adjustYoutubeUrl = url => {
    const shortsRegex =
      /https:\/\/www\.youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/;
    const incorrectWatchRegex =
      /https:\/\/www\.youtube\.com\/watch\/([a-zA-Z0-9_-]{11})/;
    const youtuBeRegex = /https:\/\/youtu\.be\/([a-zA-Z0-9_-]{11})/;

    if (shortsRegex.test(url)) {
      return url.replace(shortsRegex, 'https://www.youtube.com/watch?v=$1');
    } else if (incorrectWatchRegex.test(url)) {
      return url.replace(
        incorrectWatchRegex,
        'https://www.youtube.com/watch?v=$1'
      );
    } else if (youtuBeRegex.test(url)) {
      return url.replace(youtuBeRegex, 'https://www.youtube.com/watch?v=$1');
    } else {
      return url;
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={MultimediaSchema}
        onSubmit={values =>
          handleSubmit(values, lodash.isEqual(values, initialValues))
        }
      >
        {({ errors, values, setFieldValue }) => {
          useEffect(() => {
            setStepStatus(prev => ({
              ...prev,
              ...values
            }));
          }, [values]);

          return (
            <FormFormik>
              <ImagesUpload
                data={data}
                setData={setData}
                handleImages={handleImages}
                type="image"
                accessor="images"
                setStepStatus={setStepStatus}
                uploading={uploadingImage}
                title="Fotos"
                replaceImagesInData={replaceImagesInData}
                parent="project"
              />

              <ImagesUpload
                data={data}
                setData={setData}
                handleImages={handleImages}
                type="houseMap"
                accessor="houseMaps"
                setStepStatus={setStepStatus}
                uploading={uploadingHouseMap}
                title="Planos"
                replaceImagesInData={replaceImagesInData}
                parent="project"
              />

              <Card.Title className="mb-3 mt-4 fs-3">Videos</Card.Title>

              {/* Videos */}
              <Row className="g-3">
                <Col xs={12} lg={6}>
                  <div className="w-100 pb-4">
                    <label className="text-center w-100">Video</label>

                    <Field
                      type="text"
                      name="video"
                      className="form-control-sm form-control"
                      onChange={e => {
                        const originalUrl = e.target.value;

                        const adjustedUrl = adjustYoutubeUrl(originalUrl);

                        setFieldValue('video', adjustedUrl);
                      }}
                    />

                    {!errors.video && !values.video && (
                      <label>
                        <small>
                          El video debe ser de <strong>Youtube o Vimeo</strong>
                        </small>
                      </label>
                    )}

                    {errors.video && (
                      <label style={{ color: '#da1717e8' }}>
                        {errors.video}
                      </label>
                    )}

                    {values.video && !errors.video && (
                      <EmbedVideo
                        setShow={bool =>
                          setShowVideos({ ...showVideos, video: bool })
                        }
                        show={showVideos.video}
                        src={values.video}
                      />
                    )}
                  </div>
                </Col>

                <Col xs={12} lg={6}>
                  <div className="w-100">
                    <label className="text-center w-100">Video 360°</label>

                    <Field
                      type="text"
                      name="video360"
                      className="form-control-sm form-control"
                      onChange={e => {
                        const originalUrl = e.target.value;

                        const adjustedUrl = adjustYoutubeUrl(originalUrl);

                        setFieldValue('video360', adjustedUrl);
                      }}
                    />

                    {!errors.video360 && !values.video360 && (
                      <label>
                        <small>
                          El video debe ser de{' '}
                          <strong>Youtube, Vimeo, Recorrido360 o Kuula</strong>
                        </small>
                      </label>
                    )}

                    {errors.video360 && (
                      <label style={{ color: '#da1717e8' }}>
                        {errors.video360}
                      </label>
                    )}

                    {/* Embebed youtube render*/}
                    {values.video360 && !errors.video360 && (
                      <EmbedVideo
                        setShow={bool =>
                          setShowVideos({ ...showVideos, video360: bool })
                        }
                        show={showVideos.video360}
                        src={values.video360}
                      />
                    )}
                  </div>
                </Col>
              </Row>

              <button
                onClick={() =>
                  Object.keys(errors).length &&
                  messageHandler(
                    'error',
                    'Uno o más campos son incorrectos. Por favor, corrija los errores para poder continuar.'
                  )
                }
                hidden
                ref={yupSubmitRef}
              ></button>
            </FormFormik>
          );
        }}
      </Formik>
    </>
  );
};

export default Multimedia;
